import React, { useState } from 'react';
import {
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import ArcaPdfStudioLayout from '../../components/arca/ArcaPdfStudioLayout';
import { appendUploadFiles, isPdfFile } from '../../components/arca/uploadItemUtils';
import { getToken } from '../../api/client';
import API_CONFIG from '../../config/api';

function baixarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function postPdfForm(path, formData) {
  const token = getToken();
  return fetch(`${API_CONFIG.BACKEND_URL}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
}

export default function ArcaPdfDividirPage() {
  const [items, setItems] = useState([]);
  const [pageCount, setPageCount] = useState(null);
  const [mode, setMode] = useState('pages');
  const [ranges, setRanges] = useState('');
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const file = items[0]?.file || null;

  const handleFilesAdd = async (files) => {
    setError('');
    const n = appendUploadFiles(files, setItems, { multiple: false, filter: isPdfFile });
    if (!n) {
      setError('Selecione um arquivo PDF.');
      return;
    }
    const f = files.find(isPdfFile) || files[0];
    setPageCount(null);
    setLoadingInfo(true);
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      const res = await postPdfForm('/api/arca/pdf/info', fd);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao ler o PDF.');
      setPageCount(data.pageCount);
    } catch (e) {
      setItems([]);
      setError(e.message || 'Erro ao ler o PDF.');
    } finally {
      setLoadingInfo(false);
    }
  };

  const clearFile = () => {
    setItems([]);
    setPageCount(null);
    setRanges('');
  };

  const handleSplit = async () => {
    setError('');
    if (!file) {
      setError('Envie um PDF primeiro.');
      return;
    }
    if (mode === 'ranges' && !String(ranges).trim()) {
      setError('Informe os intervalos (ex.: 1-3,5,7-9).');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('mode', mode);
      if (mode === 'ranges') fd.append('ranges', ranges.trim());

      const res = await postPdfForm('/api/arca/pdf/split', fd);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Erro ao dividir (${res.status}).`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = match
        ? decodeURIComponent(match[1].replace(/"/g, ''))
        : mode === 'pages'
          ? `dividido-${Date.now()}.zip`
          : `extraido-${Date.now()}.pdf`;
      baixarBlob(blob, filename);
    } catch (e) {
      console.error('[ArcaPdfDividir]', e);
      setError(e.message || 'Erro ao dividir PDF.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ArcaPdfStudioLayout
      title="Dividir PDF"
      info="Envie um PDF, escolha o modo e baixe o resultado. Clique no thumbnail para visualizar · ↻ para girar."
      limits={
        <>
          Limite: <strong>50 MB</strong> · 1 arquivo.
          {pageCount != null ? (
            <>
              {' '}
              · <Chip size="small" color="primary" label={`${pageCount} pág.`} sx={{ ml: 0.5 }} />
            </>
          ) : null}
          {loadingInfo ? ' · Lendo páginas…' : null}
        </>
      }
      error={error}
      onClearError={() => setError('')}
      onRotateError={setError}
      items={items}
      setItems={setItems}
      loading={loading || loadingInfo}
      enableSortAlpha={false}
      dropzone={{
        accept: 'application/pdf,.pdf',
        multiple: false,
        maxSizeMb: 50,
        onFilesAdd: handleFilesAdd,
        label: 'Arraste um PDF ou clique para selecionar',
        hint: 'Máximo 50MB. Após o upload, escolha o modo na barra lateral.',
      }}
      sidebarExtras={
        file ? (
          <>
            <FormControl component="fieldset" disabled={loading} sx={{ mb: 2, width: '100%' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Modo
              </Typography>
              <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
                <FormControlLabel
                  value="pages"
                  control={<Radio size="small" />}
                  label="Um PDF por página (ZIP)"
                />
                <FormControlLabel
                  value="ranges"
                  control={<Radio size="small" />}
                  label="Extrair intervalos"
                />
              </RadioGroup>
            </FormControl>
            {mode === 'ranges' && (
              <TextField
                fullWidth
                size="small"
                label="Intervalos"
                placeholder="Ex.: 1-3,5,7-9"
                value={ranges}
                onChange={(e) => setRanges(e.target.value)}
                disabled={loading}
                helperText={
                  pageCount
                    ? `Páginas de 1 a ${pageCount}. Separe por vírgula.`
                    : 'Separe intervalos por vírgula.'
                }
                sx={{ mb: 2 }}
              />
            )}
          </>
        ) : null
      }
      actionLabel="Dividir e baixar"
      actionLoadingLabel="Dividindo…"
      actionStartIcon={<ContentCutOutlinedIcon />}
      onAction={handleSplit}
      actionDisabled={!file}
      onClear={clearFile}
    />
  );
}
